/**
 * SavoirCarePatientCard.jsx — one patient card on the SAVOIR CARE ED flow board.
 *
 * What: Renders a single board entry. Two visual modes: a normal clinical card
 *       (ESI chip, flag, Advance + Redirect actions, drag handle) and a terminal
 *       Patient-Elected Redirect card (muted, gray, non-draggable, no actions).
 * Why:  The card is the primary interaction surface. The "Advance" button is the
 *       primary movement path; drag-and-drop is an enhancement.
 * Invariants:
 *   - Redirect cards are a terminal state: not draggable, cannot advance, show a
 *     gray "Pt-elected" badge instead of an ESI chip.
 *   - Redirect copy must never imply clinical clearance.
 *   - Time shown is time since arrival (sessionCreatedAt/createdAt), not time in
 *     the current stage.
 *
 * Ported from HealthDesk Experimental (Exp-B) — no adaptation (tokens resolve via
 * @healthdesk/shared-utils in MinusOne).
 * Tightest coupling: SavoirCareBoardColumn.jsx (renders these), PatientElectedRedirectModal.
 */

import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  TextField,
  Stack,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowForwardRounded,
  FlagRounded,
  DragIndicatorRounded,
  LogoutRounded,
  ReportProblemRounded,
  HealthAndSafetyRounded,
  MoreVertRounded,
  LocalHospitalRounded,
  MedicalServicesRounded,
  EditRounded,
  CheckRounded,
  CloseRounded,
  PlaceRounded,
} from '@mui/icons-material';
import { tokens } from '@healthdesk/shared-utils';
import LiveEtaDisplay from 'features/careverify/components/LiveEtaDisplay';
import {
  SAVOIR_CARE_STAGES,
  ESCALATION_STAGE,
  getNextClinicalStage,
  isRedirectEligible,
  canAdvanceFromExpected,
} from '../constants/savoirCareStages';
import {
  getStageAccentColor,
  getLocationPillColors,
  getLoungeAccent,
} from '../utils/savoirCareTheme';

/** Manual clinician flag dot colors. */
const FLAG_DOT_COLORS = {
  red: tokens.clinical.critical.light,
  amber: tokens.clinical.medium.light,
  green: tokens.colors.msiGreen.light,
};

/** ESI level → chip color. ESI 1 most acute (red) … ESI 5 least acute (blue). */
const ESI_CHIP_COLORS = {
  1: tokens.clinical.critical.light,
  2: tokens.clinical.high.light,
  3: tokens.clinical.medium.light,
  4: tokens.clinical.low.light,
  5: tokens.clinical.low.light,
};

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;

/** Max characters shown on the location pill before ellipsis. */
const LOCATION_PILL_MAX_CHARS = 22;

/** Static location suggestions for the inline editor (not yet configurable). */
const LOCATION_SUGGESTIONS = ['Bay', 'Results Lounge', 'Procedure Room', 'Trauma', 'Fast Track'];

/** Truncate the pill label to a fixed width with an ellipsis. */
const truncateLocation = (value) =>
  value && value.length > LOCATION_PILL_MAX_CHARS
    ? `${value.slice(0, LOCATION_PILL_MAX_CHARS)}…`
    : value;

/** Format minutes-since-arrival as a compact "Xm" / "Xh Ym" label. */
const formatSinceArrival = (startDate, nowMs) => {
  if (!startDate) return '—';
  const start = startDate instanceof Date ? startDate.getTime() : new Date(startDate).getTime();
  if (Number.isNaN(start)) return '—';
  const mins = Math.max(0, Math.floor((nowMs - start) / MS_PER_MINUTE));
  if (mins < MINUTES_PER_HOUR) return `${mins}m`;
  const h = Math.floor(mins / MINUTES_PER_HOUR);
  const m = mins % MINUTES_PER_HOUR;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const SavoirCarePatientCard = ({
  entry,
  canWrite,
  now,
  onAdvance,
  onDismissExpected,
  onFlag,
  onEscalate,
  onRedirect,
  onFlagEmergencyIntervention,
  onClearEmergencyIntervention,
  onOpenChartMind,
  onSetLocation,
}) => {
  const theme = useTheme();
  const [flagAnchor, setFlagAnchor] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  // Inline physical_location editor (expanded/write card only).
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationDraft, setLocationDraft] = useState('');

  const isRedirect = entry.savoir_care_stage === SAVOIR_CARE_STAGES.PATIENT_ELECTED_REDIRECT;
  const isIncoming = entry.savoir_care_stage === SAVOIR_CARE_STAGES.INCOMING;
  const isExpected = entry.savoir_care_stage === SAVOIR_CARE_STAGES.EXPECTED;
  // Emergency intervention: a deteriorating patient flagged by a write-role
  // clinician (or auto-prompted at ESI 1). The red card is the signal; the
  // intervention NAME is the information. Terminal redirect cards never show as emergency.
  const isEmergency = entry.emergency_intervention === true && !isRedirect;
  const isComplete = entry.savoir_care_stage === SAVOIR_CARE_STAGES.COMPLETE;
  const isEscalationStage = entry.savoir_care_stage === ESCALATION_STAGE;
  const isEscalated = Boolean(entry.escalated);
  const canAdvance =
    canWrite &&
    !isRedirect &&
    (isExpected
      ? canAdvanceFromExpected(entry)
      : Boolean(getNextClinicalStage(entry.savoir_care_stage)));
  const canRedirect = canWrite && isRedirectEligible(entry.savoir_care_stage);
  const canDismissExpected =
    canWrite && isExpected && entry.visitType === 'elective';
  // Escalation can be raised only from the Initial review stage; it can be cleared
  // whenever it is currently set (e.g. after the physician has reviewed).
  const canToggleEscalation = canWrite && !isRedirect && (isEscalationStage || isEscalated);

  // Physical location: display-only label, never drives clinical logic.
  const physicalLocation = entry.physical_location || null;
  const isLounge = Boolean(physicalLocation) && physicalLocation.toLowerCase().includes('lounge');
  // ChartMind deep-link: an existing linked encounter switches the action to "View".
  const hasChartmindEncounter = Boolean(entry.chartmindEncounterId);

  const openLocationEditor = () => {
    setLocationDraft(physicalLocation || '');
    setEditingLocation(true);
  };
  const saveLocation = () => {
    onSetLocation?.(entry, locationDraft);
    setEditingLocation(false);
  };
  const cancelLocation = () => {
    setEditingLocation(false);
    setLocationDraft('');
  };

  // Drag is disabled for terminal redirect, Expected, Incoming, and read-only.
  const draggingDisabled = isRedirect || isIncoming || isExpected || !canWrite;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.entryId,
    data: { stage: entry.savoir_care_stage },
    disabled: draggingDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Time since arrival: arrivedAt (board arrival) first, then CareStart session
  // start, then doc creation — never time in the current stage.
  const arrivalLabel = formatSinceArrival(
    entry.arrivedAt || entry.sessionCreatedAt || entry.createdAt,
    now
  );

  // Left-border accent. Emergency takes priority (red). Then gray (redirect) /
  // teal (expected) / purple (incoming) / red (escalated) / lounge amber / none.
  const stageAccent = getStageAccentColor(theme, { isExpected, isIncoming, isRedirect });
  const loungeAccent = getLoungeAccent(theme);
  const leftAccent = isEmergency
    ? tokens.clinical.critical.light
    : stageAccent
      || (isEscalated ? tokens.clinical.critical.light : null)
      || (isLounge ? loungeAccent : null);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      elevation={0}
      data-testid={`savoir-care-card-${entry.entryId}`}
      data-stage={entry.savoir_care_stage}
      data-visit-type={entry.visitType || ''}
      data-emergency={isEmergency ? 'true' : 'false'}
      data-flag={entry.flagColor || ''}
      sx={{
        mb: 1.5,
        // Emergency cards are red all around (the red card IS the emergency signal —
        // no text label). Other states keep the single left-accent treatment.
        border: isEmergency ? `1.5px solid ${tokens.clinical.critical.light}` : '1px solid',
        borderColor: isEmergency ? tokens.clinical.critical.light : 'divider',
        borderLeft: leftAccent ? `3px solid ${leftAccent}` : '1px solid',
        borderLeftColor: leftAccent || 'divider',
        bgcolor: isEmergency
          ? alpha(theme.palette.error.main, 0.06)
          : (isRedirect || isIncoming || isExpected) ? 'action.hover' : 'background.paper',
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Header: badge/ESI + CareStart badge + flag/escalation dot + time + drag handle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          {isRedirect ? (
            <Chip
              label="Pt-elected"
              size="small"
              sx={{ bgcolor: stageAccent, color: 'common.white', fontWeight: 600 }}
            />
          ) : isExpected ? (
            <Chip
              label="Expected"
              size="small"
              sx={{ bgcolor: stageAccent, color: 'common.white', fontWeight: 600 }}
            />
          ) : isIncoming ? (
            <Chip
              label="Incoming"
              size="small"
              sx={{ bgcolor: stageAccent, color: 'common.white', fontWeight: 600 }}
            />
          ) : entry.savoir_care_esi ? (
            <Chip
              label={`ESI ${entry.savoir_care_esi}`}
              size="small"
              sx={{
                bgcolor: ESI_CHIP_COLORS[entry.savoir_care_esi] || tokens.clinical.low.light,
                color: 'common.white',
                fontWeight: 600,
              }}
            />
          ) : (
            <Chip label="ESI —" size="small" variant="outlined" />
          )}

          {entry.visitType === 'urgent' && (
            <Chip
              label="Urgent"
              size="small"
              color="error"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
              data-testid={`savoir-care-visit-urgent-${entry.entryId}`}
            />
          )}
          {entry.visitType === 'elective' && (
            <Chip
              label="Elective"
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
              data-testid={`savoir-care-visit-elective-${entry.entryId}`}
            />
          )}

          {/* CareStart-sourced patients carry a pre-encounter prep badge. */}
          {entry.hasCareStartData && (
            <Tooltip title="Pre-arrival CareStart packet available">
              <Chip
                icon={<HealthAndSafetyRounded sx={{ fontSize: '0.9rem' }} />}
                label="CareStart"
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  borderColor: theme.palette.audience?.clinician?.main,
                  color: theme.palette.audience?.clinician?.main,
                }}
              />
            </Tooltip>
          )}

          {/* Physical location pill: display-only, sits alongside the ESI chip.
              Color is by substring (lounge/trauma/procedure/other); never clinical. */}
          {physicalLocation && (() => {
            const pillColors = getLocationPillColors(theme, physicalLocation);
            return (
            <Tooltip title={physicalLocation}>
              <Chip
                icon={<PlaceRounded sx={{ fontSize: '0.8rem' }} />}
                label={truncateLocation(physicalLocation)}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  bgcolor: pillColors?.bg,
                  color: pillColors?.color,
                  border: '1px solid',
                  borderColor: pillColors?.border,
                  '& .MuiChip-icon': { color: pillColors?.color },
                }}
              />
            </Tooltip>
            );
          })()}

          {/* Escalation indicator: red dot when an APP has flagged for physician review. */}
          {isEscalated && (
            <Tooltip title="Escalated to attending physician">
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: tokens.clinical.critical.light,
                }}
              />
            </Tooltip>
          )}

          <Box
            aria-hidden
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: isRedirect
                ? stageAccent
                : FLAG_DOT_COLORS[entry.flagColor] || 'transparent',
              border: isRedirect || entry.flagColor ? 'none' : '1px solid',
              borderColor: 'divider',
            }}
          />

          <Box sx={{ flex: 1 }} />

          <Typography variant="caption" color="text.secondary">
            {arrivalLabel}
          </Typography>

          {!draggingDisabled && (
            <Tooltip title="Drag to move">
              <IconButton
                size="small"
                {...attributes}
                {...listeners}
                sx={{ cursor: 'grab', touchAction: 'none' }}
                aria-label="Drag to move card"
              >
                <DragIndicatorRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Emergency intervention name — shown in place of any "emergency" label.
            The red card already signals the emergency; this is the clinically useful
            information (e.g. "Cardioversion"). */}
        {isEmergency && entry.savoir_care_intervention_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <LocalHospitalRounded sx={{ fontSize: '1rem', color: tokens.clinical.critical.light }} />
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: tokens.clinical.critical.light }}
            >
              {entry.savoir_care_intervention_name}
            </Typography>
          </Box>
        )}

        {/* Chief complaint */}
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {entry.chiefComplaintSummary || 'Patient'}
        </Typography>
        {entry.patientAge != null && (
          <Typography variant="caption" color="text.secondary">
            Age {entry.patientAge}
          </Typography>
        )}

        {/* CareVerify ETA slot — Expected column (PROMPT 10 / §13) */}
        {isExpected && (
          <Box sx={{ mt: 0.5 }} data-testid={`careverify-eta-slot-${entry.entryId}`}>
            <LiveEtaDisplay
              liveEta={entry.liveEta}
              declaredEta={entry.declaredEta}
              variant="compact"
            />
          </Box>
        )}

        {/* Terminal redirect note (must not imply clinical clearance) */}
        {isRedirect && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
            Patient chose next-day appt. Offered full eval — declined.
          </Typography>
        )}

        {/* Actions (clinical cards only) */}
        {!isRedirect && canWrite && (
          <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<ArrowForwardRounded />}
              onClick={() => onAdvance(entry)}
              disabled={!canAdvance}
              data-testid={`savoir-care-advance-${entry.entryId}`}
            >
              {isExpected
                ? 'Mark arriving'
                : isIncoming
                  ? 'Begin triage'
                  : isComplete
                    ? 'Done'
                    : 'Advance'}
            </Button>

            {canDismissExpected && (
              <Button
                size="small"
                onClick={() => onDismissExpected?.(entry)}
                data-testid={`savoir-care-dismiss-expected-${entry.entryId}`}
              >
                Dismiss
              </Button>
            )}

            {canRedirect && (
              <Tooltip title="Document patient-elected redirect">
                <IconButton
                  size="small"
                  onClick={() => onRedirect(entry)}
                  aria-label="Patient-elected redirect"
                  data-testid={`savoir-care-redirect-${entry.entryId}`}
                >
                  <LogoutRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* APP escalation to attending physician (raise from stage 4; clear anytime set). */}
            {canToggleEscalation && (
              <Tooltip title={isEscalated ? 'Clear escalation' : 'Escalate to attending physician'}>
                <IconButton
                  size="small"
                  onClick={() => onEscalate?.(entry, !isEscalated)}
                  aria-label={isEscalated ? 'Clear escalation' : 'Escalate to attending physician'}
                  data-testid={`savoir-care-escalate-${entry.entryId}`}
                >
                  <ReportProblemRounded
                    fontSize="small"
                    sx={{ color: isEscalated ? tokens.clinical.critical.light : 'text.disabled' }}
                  />
                </IconButton>
              </Tooltip>
            )}

            <Box sx={{ flex: 1 }} />

            <Tooltip title="Flag">
              <IconButton
                size="small"
                onClick={(e) => setFlagAnchor(e.currentTarget)}
                aria-label="Set flag color"
              >
                <FlagRounded
                  fontSize="small"
                  sx={{ color: FLAG_DOT_COLORS[entry.flagColor] || 'text.disabled' }}
                />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={flagAnchor} open={Boolean(flagAnchor)} onClose={() => setFlagAnchor(null)}>
              {['red', 'amber', 'green'].map((color) => (
                <MenuItem
                  key={color}
                  onClick={() => {
                    onFlag(entry, color);
                    setFlagAnchor(null);
                  }}
                >
                  <Box
                    sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: FLAG_DOT_COLORS[color], mr: 1 }}
                  />
                  {color.charAt(0).toUpperCase() + color.slice(1)}
                </MenuItem>
              ))}
              <MenuItem
                onClick={() => {
                  onFlag(entry, null);
                  setFlagAnchor(null);
                }}
              >
                Clear flag
              </MenuItem>
            </Menu>

            {/* Overflow menu: emergency intervention flag/clear + ChartMind. */}
            <Tooltip title="More actions">
              <IconButton
                size="small"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                aria-label="More actions"
              >
                <MoreVertRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem
                onClick={() => {
                  onOpenChartMind?.(entry);
                  setMenuAnchor(null);
                }}
              >
                <MedicalServicesRounded fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                {hasChartmindEncounter ? 'View encounter' : 'Open in ChartMind'}
              </MenuItem>
              {isEmergency ? (
                <MenuItem
                  key="clear-intervention"
                  onClick={() => {
                    onClearEmergencyIntervention?.(entry);
                    setMenuAnchor(null);
                  }}
                >
                  <LocalHospitalRounded fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} />
                  Clear intervention flag
                </MenuItem>
              ) : (
                // Emergency intervention is flagged via this overflow action OR automatically
                // prompted when ESI 1 is assigned during manual Add Patient. There is
                // intentionally no per-card ESI reassignment control — if a patient
                // deteriorates mid-visit, this overflow action is the correct path.
                <MenuItem
                  key="flag-intervention"
                  onClick={() => {
                    onFlagEmergencyIntervention?.(entry);
                    setMenuAnchor(null);
                  }}
                >
                  <LocalHospitalRounded
                    fontSize="small"
                    sx={{ mr: 1, color: tokens.clinical.critical.light }}
                  />
                  Flag emergency intervention
                </MenuItem>
              )}
            </Menu>
          </Box>

          {/* Footer: ChartMind deep-link (labeled action) + physical_location editor. */}
          <Box sx={{ mt: 0.75 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
              <Button
                size="small"
                variant="text"
                startIcon={<MedicalServicesRounded />}
                onClick={() => onOpenChartMind?.(entry)}
                sx={{ textTransform: 'none', fontSize: '0.72rem', minWidth: 0 }}
              >
                {hasChartmindEncounter ? 'View encounter' : 'Open in ChartMind'}
              </Button>

              {!editingLocation && (
                physicalLocation ? (
                  <Tooltip title="Edit location">
                    <IconButton size="small" onClick={openLocationEditor} aria-label="Edit location">
                      <EditRounded sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    startIcon={<PlaceRounded sx={{ fontSize: 14 }} />}
                    onClick={openLocationEditor}
                    data-testid={`savoir-care-location-add-${entry.entryId}`}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary', minWidth: 0 }}
                  >
                    + Location
                  </Button>
                )
              )}
            </Stack>

            {/* Inline location editor (no modal): pre-filled, suggestion chips, Enter/Esc. */}
            {editingLocation && (
              <Box sx={{ mt: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <TextField
                    value={locationDraft}
                    onChange={(e) => setLocationDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveLocation();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelLocation();
                      }
                    }}
                    placeholder="e.g. Bay 4"
                    size="small"
                    autoFocus
                    fullWidth
                    inputProps={{
                      maxLength: 40,
                      'aria-label': 'Physical location',
                      'data-testid': `savoir-care-location-input-${entry.entryId}`,
                    }}
                    sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }}
                  />
                  <Tooltip title="Save location">
                    <IconButton
                      size="small"
                      onClick={saveLocation}
                      aria-label="Save location"
                      data-testid={`savoir-care-location-save-${entry.entryId}`}
                    >
                      <CheckRounded sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton size="small" onClick={cancelLocation} aria-label="Cancel location edit">
                      <CloseRounded sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {LOCATION_SUGGESTIONS.map((suggestion) => (
                    <Chip
                      key={suggestion}
                      label={suggestion}
                      size="small"
                      variant="outlined"
                      onClick={() => setLocationDraft(suggestion)}
                      sx={{ height: 20, fontSize: '0.62rem', cursor: 'pointer' }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SavoirCarePatientCard;
